import React, { useEffect, useState } from "react";
import {
  AutoAwesome,
  CheckCircleOutline,
  Download,
  PhoneAndroid,
  Security,
  Watch,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import "../styles/components/DownloadsPage.css";

const GIST_BASE_URL =
  "https://gist.githubusercontent.com/coryevansmith/eaa68b025a73e42710077bcbc82ece74/raw";
const UNIVERSAL_AI_PROMPT_FILE = "no-bull-lifting-universal-ai-prompt.txt";

const downloadOptions = [
  {
    key: "phone",
    title: "Android phone app",
    eyebrow: "PHONE & TABLET",
    description:
      "Build workouts, import AI-generated plans, track sessions, review progress, and sync with your watch.",
    icon: PhoneAndroid,
    manifestUrl: `${GIST_BASE_URL}/update-manifest.json`,
    fallbackVersion: "1.4.0",
    fileName: "no-bull-lifting-phone.apk",
    requirements: "Android 8.0 or newer",
    buttonLabel: "Download phone APK",
  },
  {
    key: "wear",
    title: "Wear OS watch app",
    eyebrow: "SMARTWATCH",
    description:
      "Log sets from your wrist, control workout timers, and keep your active workout synchronized with the phone app.",
    icon: Watch,
    manifestUrl: `${GIST_BASE_URL}/wear-update-manifest.json`,
    fallbackVersion: "1.2.0",
    fileName: "no-bull-lifting-wear.apk",
    requirements: "Wear OS 3.0 or newer",
    buttonLabel: "Download Wear OS APK",
  },
];

function downloadUrl(fileName) {
  return `${process.env.PUBLIC_URL || ""}/downloads/${fileName}`;
}

function releaseNoteItems(notes) {
  if (!notes) return [];

  return notes
    .split("\n")
    .map((note) => note.replace(/^[\s•-]+/, "").trim())
    .filter(Boolean);
}

function DownloadCard({ option, manifest, loading }) {
  const Icon = option.icon;
  const version = manifest?.versionName || option.fallbackVersion;
  const releaseNotes = releaseNoteItems(manifest?.releaseNotes);

  return (
    <Paper className={`download-card download-card-${option.key}`} elevation={0}>
      <Box className="download-card-heading">
        <Box className="download-platform-icon" aria-hidden="true">
          <Icon />
        </Box>
        <Box>
          <Typography className="download-eyebrow" component="p">
            {option.eyebrow}
          </Typography>
          <Typography variant="h4" component="h2" className="download-title">
            {option.title}
          </Typography>
        </Box>
      </Box>

      <Typography className="download-description">
        {option.description}
      </Typography>

      <Box className="download-meta">
        <Chip
          label={loading ? "Checking version…" : `Version ${version}`}
          icon={loading ? <CircularProgress size={14} /> : <CheckCircleOutline />}
          size="small"
        />
        <Typography component="span">{option.requirements}</Typography>
      </Box>

      {releaseNotes.length > 0 && (
        <Box className="release-notes">
          <Typography component="h3">What’s new</Typography>
          <ul>
            {releaseNotes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </Box>
      )}

      <Button
        className="apk-download-button"
        variant="contained"
        size="large"
        startIcon={<Download />}
        href={downloadUrl(option.fileName)}
        download={option.fileName}
      >
        {option.buttonLabel}
      </Button>
    </Paper>
  );
}

function Downloads() {
  const [manifests, setManifests] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    Promise.all(
      downloadOptions.map(async (option) => {
        const response = await fetch(option.manifestUrl, { cache: "no-store" });
        if (!response.ok) throw new Error(`Unable to load ${option.key} version`);
        return [option.key, await response.json()];
      })
    )
      .then((entries) => {
        if (active) setManifests(Object.fromEntries(entries));
      })
      .catch(() => {
        // The stable download links and bundled version labels remain usable.
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <Box className="downloads-page">
      <section className="downloads-hero">
        <Container maxWidth="lg">
          <Box className="downloads-brand-lockup">
            <img
              src={`${process.env.PUBLIC_URL || ""}/no-bull-lifting-logo.png`}
              alt="No Bull Lifting muscular bull lifting a barbell"
              className="downloads-logo"
            />
            <Typography className="downloads-kicker" component="p">
              NO BULL LIFTING
            </Typography>
          </Box>
          <Typography variant="h1" component="h1">
            Train anywhere.
            <br />
            <span>Log everything.</span>
          </Typography>
          <Typography className="downloads-lede">
            An offline-first workout logger for Android and Wear OS, built for
            straightforward training without subscriptions or distractions.
          </Typography>
          <Box className="hero-pills" aria-label="Application highlights">
            <span>Offline first</span>
            <span>Phone + watch sync</span>
            <span>AI-ready imports</span>
          </Box>
        </Container>
      </section>

      <Container maxWidth="lg" className="downloads-content">
        <Box className="downloads-intro">
          <Typography variant="h3" component="h2">
            Choose your device
          </Typography>
          <Typography>
            Install the phone app first, then add the companion app to a paired
            Wear OS watch. Both downloads are replaced in place whenever a new
            release is available.
          </Typography>
        </Box>

        <Paper className="gpt-callout" elevation={0}>
          <Box className="gpt-callout-icon" aria-hidden="true">
            <AutoAwesome />
          </Box>
          <Box className="gpt-callout-copy">
            <Typography className="download-eyebrow" component="p">
              FREE AI WORKOUT PROMPT
            </Typography>
            <Typography variant="h4" component="h2">
              Generate workouts with any AI
            </Typography>
            <Typography>
              Download the model-neutral prompt, give it to the AI assistant
              you already use, and receive a validated JSON workout or program
              ready for No Bull Lifting. No public GPT or subscription to this
              app is required.
            </Typography>
          </Box>
          <Button
            className="gpt-link-button"
            variant="contained"
            href={downloadUrl(UNIVERSAL_AI_PROMPT_FILE)}
            download={UNIVERSAL_AI_PROMPT_FILE}
            startIcon={<Download />}
          >
            Download universal AI prompt
          </Button>

          <ol className="ai-prompt-steps">
            <li>
              <span>1</span>
              Download and paste or attach the prompt in any AI assistant.
            </li>
            <li>
              <span>2</span>
              Describe your goals, schedule, equipment, and limitations.
            </li>
            <li>
              <span>3</span>
              Save its JSON file, then import and review it in the phone app.
            </li>
          </ol>
        </Paper>

        <Grid container spacing={3} alignItems="stretch">
          {downloadOptions.map((option) => (
            <Grid item xs={12} md={6} key={option.key}>
              <DownloadCard
                option={option}
                manifest={manifests[option.key]}
                loading={loading}
              />
            </Grid>
          ))}
        </Grid>

        <Alert
          className="install-notice"
          severity="info"
          icon={<Security />}
        >
          <Typography component="h2">Installing an APK</Typography>
          <Typography component="p">
            Android may ask you to allow installs from your browser or file
            manager. Approve the prompt for this download, open the APK, and
            choose Install. Existing workout data remains in place when
            updating a signed version of the app.
          </Typography>
        </Alert>

        <Paper className="watch-steps" elevation={0}>
          <Box>
            <Typography className="download-eyebrow" component="p">
              WATCH SETUP
            </Typography>
            <Typography variant="h4" component="h2">
              Install the companion app
            </Typography>
          </Box>
          <ol>
            <li>
              <span>1</span>
              Install No Bull Lifting on your Android phone.
            </li>
            <li>
              <span>2</span>
              Install the Wear OS APK on your paired watch for the first setup.
            </li>
            <li>
              <span>3</span>
              Future watch updates can be downloaded and sent from the phone
              app’s More page.
            </li>
          </ol>
        </Paper>
      </Container>

      <footer className="downloads-footer">
        <Container maxWidth="lg">
          <img
            src={`${process.env.PUBLIC_URL || ""}/no-bull-lifting-logo.png`}
            alt=""
            aria-hidden="true"
          />
          <Typography>No Bull Lifting</Typography>
          <Typography component="span">© {new Date().getFullYear()} Cory Smith</Typography>
        </Container>
      </footer>
    </Box>
  );
}

export default Downloads;
